import { useEffect, useRef, useState, Fragment } from "react";
import { Plus, Loader2, Pencil, Trash2, Search, Upload, X, FileSpreadsheet, Download, CheckCircle2, FileText, Images, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import { api, apiError, BACKEND_URL } from "@/lib/api";
import { imgUrl, fmtTL, fmtGram } from "@/lib/format";
import { compressImage } from "@/lib/imageCompress";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { SubcategoryInput } from "@/components/SubcategoryInput";

const empty = {
  name: "", code: "", category: "", subcategory: "", subcategories: [], subsub: "", brand: "", model: "", karat: "14",
  gram: "", gram_min: "", gram_max: "", sizes: "", milyem: "", barcode: "", image_url: "", tray: "", group: "", images: [], is_set: false, set_parts: [],
  stock_status: "Stokta", active: true,
};

export default function AdminProducts({ fixedGroup = "" }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [q, setQ] = useState("");
  const [trayFilter, setTrayFilter] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [subFilter, setSubFilter] = useState("");
  const [delTarget, setDelTarget] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const openLightbox = (p) => {
    const imgs = Array.from(new Set([p.image_url, ...(p.images || [])].filter(Boolean))).map(imgUrl);
    if (!imgs.length) return;
    setLightbox({ images: imgs, idx: 0 });
  };
  const lbNav = (dir) => setLightbox((lb) => lb && ({ ...lb, idx: (lb.idx + dir + lb.images.length) % lb.images.length }));
  const [selected, setSelected] = useState(new Set());
  const [bulkDelOpen, setBulkDelOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bEdit, setBEdit] = useState({});
  const [aiTag, setAiTag] = useState({ busy: false, done: 0, total: 0 });
  const [wmBusy, setWmBusy] = useState(false);
  const applyWatermark = async () => {
    if (!window.confirm("Mevcut ürün görsellerine logo (filigran) eklensin mi? Zaten logolu görseller atlanır.")) return;
    setWmBusy(true);
    try {
      const { data } = await api.post("/admin/watermark-existing");
      toast.success(`${data.watermarked} görsele logo eklendi (${data.skipped} atlandı)`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "İşlem başarısız");
    } finally {
      setWmBusy(false);
    }
  };
  const fileRef = useRef();

  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dlTemplate, setDlTemplate] = useState(false);
  const [dlPdf, setDlPdf] = useState(false);
  const [dlCatalog, setDlCatalog] = useState(false);
  const [pdfCat, setPdfCat] = useState("all");
  const [pdfSub, setPdfSub] = useState("");
  const importRef = useRef();

  const downloadPdf = async () => {
    setDlPdf(true);
    try {
      const scope = fixedGroup === "6007" ? "all" : pdfCat;
      const sub = fixedGroup === "6007" ? "" : pdfSub;
      const res = await api.get("/admin/products/export-pdf", { params: { category: scope, subcategory: sub }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const base = scope === "all" ? "tum-urunler" : scope.toLowerCase().replace(/[^a-z0-9]/g, "-");
      const safe = base + (sub ? "-" + sub.toLowerCase().replace(/[^a-z0-9]/g, "-") : "");
      a.download = `orse-gold-${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF indirildi");
    } catch (err) {
      const detail = err.response?.status === 404 ? "Bu kapsamda ürün bulunamadı" : apiError(err.response?.data?.detail);
      toast.error(detail);
    } finally {
      setDlPdf(false);
    }
  };

  const [dlStock, setDlStock] = useState(false);
  const downloadStockSheet = async () => {
    setDlStock(true);
    try {
      const scope = fixedGroup === "6007" ? "all" : pdfCat;
      const sub = fixedGroup === "6007" ? "" : pdfSub;
      const res = await api.get("/admin/stock-sheet-pdf", { params: { category: scope, subcategory: sub }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "orse-gold-stok-foyu.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Stok föyü indirildi");
    } catch (err) {
      const detail = err.response?.status === 404 ? "Bu kapsamda ürün bulunamadı" : apiError(err.response?.data?.detail);
      toast.error(detail);
    } finally {
      setDlStock(false);
    }
  };

  const downloadCatalog = async () => {
    setDlCatalog(true);
    try {
      const scope = fixedGroup === "6007" ? "all" : pdfCat;
      const sub = fixedGroup === "6007" ? "" : pdfSub;
      const res = await api.get("/catalog-pdf", { params: { category: scope, subcategory: sub }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const base = scope === "all" ? "tum-urunler" : scope.toLowerCase().replace(/[^a-z0-9]/g, "-");
      const safe = base + (sub ? "-" + sub.toLowerCase().replace(/[^a-z0-9]/g, "-") : "");
      a.download = `orse-gold-katalog-${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Katalog indirildi");
    } catch (err) {
      const detail = err.response?.status === 404 ? "Bu kapsamda ürün bulunamadı" : apiError(err.response?.data?.detail);
      toast.error(detail);
    } finally {
      setDlCatalog(false);
    }
  };

  const downloadTemplate = async () => {
    setDlTemplate(true);
    try {
      const res = await api.get("/admin/products/import-template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "orsegold_urun_sablonu.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Şablon indirilemedi");
    } finally {
      setDlTemplate(false);
    }
  };

  const runImport = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`${data.added} eklendi, ${data.updated} güncellendi`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "İçe aktarma başarısız");
    } finally {
      setImporting(false);
      if (importRef.current) importRef.current.value = "";
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([api.get("/admin/products"), api.get("/categories")]);
      setProducts(p.data);
      setCategories(c.data);
      setSelected(new Set());
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [aiJob, setAiJob] = useState(null);
  const startAnalyzeAll = async () => {
    try {
      await api.post("/admin/ai/analyze-all");
      toast.info("AI analizi başlatıldı…");
      const poll = setInterval(async () => {
        try {
          const { data } = await api.get("/admin/ai/analyze-status");
          setAiJob(data);
          if (!data.running) {
            clearInterval(poll);
            setAiJob(null);
            toast.success(`AI analizi tamamlandı (${data.done}/${data.total})`);
            load();
          }
        } catch { clearInterval(poll); setAiJob(null); }
      }, 3000);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Analiz başlatılamadı");
    }
  };

  const [embJob, setEmbJob] = useState(null);
  const startReindex = async () => {
    try {
      await api.post("/admin/ai/reindex-embeddings");
      toast.info("Anlamsal arama indeksi başlatıldı…");
      const poll = setInterval(async () => {
        try {
          const { data } = await api.get("/admin/ai/reindex-embeddings/status");
          setEmbJob(data);
          if (!data.running) {
            clearInterval(poll);
            setEmbJob(null);
            toast.success(`Anlamsal indeks tamamlandı (${data.done}/${data.total})`);
          }
        } catch { clearInterval(poll); setEmbJob(null); }
      }, 3000);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "İndeksleme başlatılamadı");
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...empty, category: categories[0]?.name || "", group: fixedGroup || "" });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...empty, ...p, gram: String(p.gram ?? ""), milyem: String(p.milyem ?? ""), gram_min: p.gram_min != null ? String(p.gram_min) : "", gram_max: p.gram_max != null ? String(p.gram_max) : "", sizes: (p.sizes || []).join(", ") });
    setOpen(true);
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const cf = await compressImage(file);
      const fd = new FormData();
      fd.append("file", cf);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setForm((f) => ({ ...f, images: [...(f.images || []), data.url].slice(0, 6) }));
      toast.success("Fotoğraf yüklendi");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Yükleme başarısız");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        gram: parseFloat(form.gram) || 0,
        gram_min: form.gram_min !== "" ? parseFloat(form.gram_min) : null,
        gram_max: form.gram_max !== "" ? parseFloat(form.gram_max) : null,
        sizes: form.sizes ? form.sizes.split(",").map((s) => s.trim()).filter(Boolean) : [],
        milyem: parseFloat(form.milyem) || 0,
        allowed_customers: [],
      };
      if (editing) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Ürün güncellendi");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Ürün eklendi");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/products/${delTarget.id}`);
      toast.success("Ürün silindi");
      setDelTarget(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const activateProduct = async (p) => {
    try {
      await api.post(`/admin/products/${p.id}/activate`);
      toast.success("Ürün yayınlandı — müşterilere görünür");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "İşlem başarısız");
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const bulkDelete = async () => {
    try {
      const ids = [...selected];
      const { data } = await api.post("/admin/products/bulk-delete", { ids });
      toast.success(`${data.deleted} ürün silindi`);
      setBulkDelOpen(false);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Silme başarısız");
    }
  };

  const bset = (k, v) => setBEdit((s) => ({ ...s, [k]: v }));
  const tagAll = async () => {
    const targets = products.filter((p) => p.image_url);
    if (!targets.length) { toast.error("Görselli ürün yok"); return; }
    if (!window.confirm(`${targets.length} ürün yapay zeka ile etiketlenecek (arama için). Devam edilsin mi?`)) return;
    setAiTag({ busy: true, done: 0, total: targets.length });
    let ok = 0;
    for (let i = 0; i < targets.length; i++) {
      try { await api.post("/admin/ai/tag-product", { product_id: targets[i].id }); ok++; } catch { /* skip */ }
      setAiTag({ busy: true, done: i + 1, total: targets.length });
    }
    setAiTag({ busy: false, done: 0, total: 0 });
    toast.success(`${ok} ürün etiketlendi (arama için)`);
  };
  const applyBulkEdit = async () => {
    const fields = {};
    if (bEdit.name && bEdit.name.trim()) fields.name = bEdit.name.trim();
    if (bEdit.karat) fields.karat = bEdit.karat;
    if (bEdit.milyem !== undefined && bEdit.milyem !== "") fields.milyem = Number(bEdit.milyem);
    if (bEdit.category) fields.category = bEdit.category;
    if (bEdit.subcategory) fields.subcategory = bEdit.subcategory;
    if (!fixedGroup && bEdit.group) fields.group = bEdit.group === "genel" ? "" : bEdit.group;
    if (bEdit.tray !== undefined && bEdit.tray !== "") fields.tray = bEdit.tray;
    if (bEdit.stock_status) fields.stock_status = bEdit.stock_status;
    if (bEdit.active) fields.active = bEdit.active === "active";
    if (Object.keys(fields).length === 0) { toast.error("Değiştirilecek en az bir alan seçin"); return; }
    try {
      const { data } = await api.post("/admin/products/bulk-update", { ids: [...selected], fields });
      toast.success(`${data.updated} ürün güncellendi`);
      setBulkEditOpen(false);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Güncelleme başarısız");
    }
  };

  const psubs = (p) => (p.subcategories?.length ? p.subcategories : (p.subcategory ? [p.subcategory] : []));
  const filtered = products.filter((p) => {
    const matchQ = !q || [p.name, p.code, p.category, p.tray].some((x) => (x || "").toLowerCase().includes(q.toLowerCase()));
    const matchTray = !trayFilter || (p.tray || "") === trayFilter;
    const matchCat = !catFilter || (p.category || "") === catFilter;
    const matchSub = !subFilter || psubs(p).includes(subFilter);
    let matchGroup = true;
    if (fixedGroup) matchGroup = (p.group || "") === fixedGroup;
    else if (groupFilter === "genel") matchGroup = !p.group;
    else if (groupFilter === "6007") matchGroup = (p.group || "") === "6007";
    return matchQ && matchTray && matchCat && matchSub && matchGroup;
  });

  const subOptions = catFilter ? (categories.find((c) => c.name === catFilter)?.subcategories || []) : [];
  const pdfSubOptions = (pdfCat && pdfCat !== "all") ? (categories.find((c) => c.name === pdfCat)?.subcategories || []) : [];

  const KARAT_ORDER = ["14", "22"];
  const karatGroups = (() => {
    const map = {};
    filtered.forEach((p) => { const k = String(p.karat || "").trim() || "Diğer"; (map[k] = map[k] || []).push(p); });
    const keys = Object.keys(map).sort((a, b) => {
      const ia = KARAT_ORDER.indexOf(a); const ib = KARAT_ORDER.indexOf(b);
      if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      return a.localeCompare(b, undefined, { numeric: true });
    });
    return keys.map((k) => ({ karat: k, items: map[k] }));
  })();

  const trays = [...new Set(products.map((p) => p.tray).filter(Boolean))].sort();

  const inputCls = "bg-obsidian border-border text-foreground focus-visible:ring-gold";

  const allSelected = filtered.length > 0 && filtered.every((p) => selected.has(p.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(filtered.map((p) => p.id)));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl text-foreground">{fixedGroup === "6007" ? "6007 Ürünleri" : "Ürünler"}</h1>
          <p className="text-sm text-muted-foreground mt-1">{filtered.length} ürün{fixedGroup === "6007" ? " · yalnızca admin" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="download-template-button" onClick={downloadTemplate} disabled={dlTemplate} className="flex items-center gap-2 px-3 h-10 border border-border rounded-md text-sm hover:border-gold transition-colors disabled:opacity-60">
            {dlTemplate ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />} <span className="hidden sm:inline">Şablon</span>
          </button>
          <button data-testid="import-products-button" onClick={() => { setImportResult(null); setImportOpen(true); }} className="flex items-center gap-2 px-3 h-10 border border-gold/40 text-gold rounded-md text-sm hover:bg-gold/10 transition-colors">
            <FileSpreadsheet size={16} /> <span className="hidden sm:inline">Toplu İçe Aktar</span>
          </button>
          {fixedGroup !== "6007" && (
            <Select value={pdfCat} onValueChange={(v) => { setPdfCat(v); setPdfSub(""); }}>
              <SelectTrigger data-testid="pdf-category-select" className={`${inputCls} h-10 w-full sm:w-40`}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-surface border-border text-foreground max-h-72">
                <SelectItem value="all">Tüm Ürünler</SelectItem>
                {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {fixedGroup !== "6007" && pdfSubOptions.length > 0 && (
            <Select value={pdfSub || "all"} onValueChange={(v) => setPdfSub(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="pdf-subcategory-select" className={`${inputCls} h-10 w-full sm:w-40`}><SelectValue placeholder="Alt Kategori" /></SelectTrigger>
              <SelectContent className="bg-surface border-border text-foreground max-h-72">
                <SelectItem value="all">Tüm Alt Kategoriler</SelectItem>
                {pdfSubOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <button data-testid="export-pdf-button" onClick={downloadPdf} disabled={dlPdf} className="flex items-center gap-2 px-3 h-10 border border-gold/40 text-gold rounded-md text-sm hover:bg-gold/10 transition-colors disabled:opacity-60">
            {dlPdf ? <Loader2 className="animate-spin" size={16} /> : <FileText size={16} />} <span className="hidden sm:inline">Liste PDF</span>
          </button>
          <button data-testid="export-catalog-button" onClick={downloadCatalog} disabled={dlCatalog} className="flex items-center gap-2 px-3 h-10 border border-gold/40 text-gold rounded-md text-sm hover:bg-gold/10 transition-colors disabled:opacity-60">
            {dlCatalog ? <Loader2 className="animate-spin" size={16} /> : <Images size={16} />} <span className="hidden sm:inline">Katalog PDF</span>
          </button>
          <button data-testid="export-stock-sheet-button" onClick={downloadStockSheet} disabled={dlStock} title="Fotoğraf + seri no'lu stok kontrol föyü (elle sayım için)" className="flex items-center gap-2 px-3 h-10 border border-gold/40 text-gold rounded-md text-sm hover:bg-gold/10 transition-colors disabled:opacity-60">
            {dlStock ? <Loader2 className="animate-spin" size={16} /> : <FileText size={16} />} <span className="hidden sm:inline">Stok Föyü</span>
          </button>
          <button data-testid="apply-watermark" onClick={applyWatermark} disabled={wmBusy} className="flex items-center gap-2 px-3 h-10 border border-gold/40 text-gold rounded-md text-sm hover:bg-gold/10 transition-colors disabled:opacity-60">
            {wmBusy ? <Loader2 className="animate-spin" size={16} /> : <Images size={16} />} <span className="hidden sm:inline">Logoyu Uygula</span>
          </button>
          <button data-testid="ai-tag-all" onClick={tagAll} disabled={aiTag.busy} className="flex items-center gap-2 px-3 h-10 border border-gold/40 text-gold rounded-md text-sm hover:bg-gold/10 transition-colors disabled:opacity-60">
            {aiTag.busy ? <><Loader2 className="animate-spin" size={16} /> {aiTag.done}/{aiTag.total}</> : <><Sparkles size={16} /> <span className="hidden sm:inline">AI Etiketle</span></>}
          </button>
          <button data-testid="add-product-button" onClick={openNew} className="flex items-center gap-2 px-4 h-10 bg-gold text-obsidian font-semibold rounded-md hover:bg-gold-hover transition-colors">
            <Plus size={18} /> <span className="hidden sm:inline">Ürün Ekle</span>
          </button>
          <button data-testid="ai-analyze-all-button" onClick={startAnalyzeAll} disabled={!!aiJob} title="Tüm ürünleri AI ile analiz et" className="flex items-center gap-2 px-3 h-10 border border-gold/40 text-gold rounded-md text-sm hover:bg-gold/10 transition-colors disabled:opacity-60">
            {aiJob ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
            <span className="hidden sm:inline">{aiJob ? `AI ${aiJob.done}/${aiJob.total}` : "Tümünü Analiz Et"}</span>
          </button>
          <button data-testid="ai-reindex-embeddings-button" onClick={startReindex} disabled={!!embJob} title="Anlamsal arama için ürün vektörlerini yeniden üret" className="flex items-center gap-2 px-3 h-10 border border-gold/40 text-gold rounded-md text-sm hover:bg-gold/10 transition-colors disabled:opacity-60">
            {embJob ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
            <span className="hidden sm:inline">{embJob ? `İndeks ${embJob.done}/${embJob.total}` : "Anlamsal İndeks"}</span>
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ürün / kod / tabla ara..." className={`pl-9 ${inputCls}`} data-testid="product-search" />
        </div>
        <Select value={catFilter || "all"} onValueChange={(v) => { setCatFilter(v === "all" ? "" : v); setSubFilter(""); }}>
          <SelectTrigger data-testid="category-filter" className={`${inputCls} h-10 w-full sm:w-48`}><SelectValue placeholder="Kategori" /></SelectTrigger>
          <SelectContent className="bg-surface border-border text-foreground max-h-72">
            <SelectItem value="all">Tüm Kategoriler</SelectItem>
            {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {subOptions.length > 0 && (
          <Select value={subFilter || "all"} onValueChange={(v) => setSubFilter(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="subcategory-filter" className={`${inputCls} h-10 w-full sm:w-48`}><SelectValue placeholder="Alt Kategori" /></SelectTrigger>
            <SelectContent className="bg-surface border-border text-foreground max-h-72">
              <SelectItem value="all">Tüm Alt Kategoriler</SelectItem>
              {subOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={trayFilter || "all"} onValueChange={(v) => setTrayFilter(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="tray-filter" className={`${inputCls} h-10 w-full sm:w-56`}><SelectValue placeholder="Tabla filtrele" /></SelectTrigger>
          <SelectContent className="bg-surface border-border text-foreground max-h-72">
            <SelectItem value="all">Tüm Tablalar</SelectItem>
            {trays.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        {!fixedGroup && (
          <Select value={groupFilter || "all"} onValueChange={(v) => setGroupFilter(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="group-filter" className={`${inputCls} h-10 w-full sm:w-44`}><SelectValue placeholder="Grup" /></SelectTrigger>
            <SelectContent className="bg-surface border-border text-foreground">
              <SelectItem value="all">Tüm Gruplar</SelectItem>
              <SelectItem value="genel">Genel</SelectItem>
              <SelectItem value="6007">6007</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {selected.size > 0 && (
        <div className="flex items-center justify-between bg-surface border border-gold/30 rounded-md px-4 py-2.5 mb-4" data-testid="bulk-delete-bar">
          <span className="text-sm text-foreground">{selected.size} ürün seçildi</span>
          <div className="flex items-center gap-2">
            <button data-testid="bulk-edit-button" onClick={() => { setBEdit({}); setBulkEditOpen(true); }} className="flex items-center gap-2 px-4 h-9 bg-gold text-obsidian font-medium rounded-md hover:bg-gold-hover transition-colors text-sm">
              <Pencil size={15} /> Toplu Düzenle
            </button>
            <button data-testid="bulk-delete-button" onClick={() => setBulkDelOpen(true)} className="flex items-center gap-2 px-4 h-9 bg-red-500/90 text-white rounded-md hover:bg-red-600 transition-colors text-sm">
              <Trash2 size={15} /> Seçilenleri Sil
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-gold" size={28} /></div>
      ) : (
        <div className="bg-surface border border-border rounded-md overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-4 py-3 w-8"><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="select-all-products" className="accent-[#c9a227] w-4 h-4 cursor-pointer" /></th>
                <th className="px-4 py-3 font-medium">Ürün</th>
                <th className="px-4 py-3 font-medium">Kategori</th>
                <th className="px-4 py-3 font-medium">Tabla</th>
                <th className="px-4 py-3 font-medium">Grup</th>
                <th className="px-4 py-3 font-medium">Ayar/Gram</th>
                <th className="px-4 py-3 font-medium">İşçilik (Milyem)</th>
                <th className="px-4 py-3 font-medium">Durum</th>
                <th className="px-4 py-3 font-medium">Stok</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {karatGroups.map((g) => (
                <Fragment key={g.karat}>
                  <tr className="bg-elevated/60"><td colSpan={10} className="px-4 py-2 text-xs font-semibold text-gold tracking-wide" data-testid={`admin-karat-${g.karat}`}>{g.karat === "Diğer" ? "Diğer Ayarlar" : `${g.karat} Ayar`} · {g.items.length}</td></tr>
                  {g.items.map((p) => (
                <tr key={p.id} data-testid={`product-row-${p.code}`} className="border-b border-border last:border-0 hover:bg-elevated/50">
                  <td className="px-4 py-3"><input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSelect(p.id)} data-testid={`select-product-${p.code}`} className="accent-[#c9a227] w-4 h-4 cursor-pointer" /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => openLightbox(p)}
                        disabled={!p.image_url && !(p.images && p.images.length)}
                        data-testid={`product-thumb-${p.code}`}
                        className="w-10 h-12 rounded overflow-hidden bg-elevated shrink-0 relative group/thumb cursor-zoom-in hover:ring-2 hover:ring-gold/60 transition disabled:cursor-default disabled:hover:ring-0"
                      >
                        {p.image_url && <img src={imgUrl(p.image_url)} alt="" className="w-full h-full object-cover" />}
                        {(p.images && p.images.length > 0) && <span className="absolute bottom-0 right-0 text-[9px] px-1 bg-black/60 text-white rounded-tl">+{p.images.length}</span>}
                      </button>
                      <div>
                        <div className="text-foreground">{p.name}</div>
                        <div className="font-mono text-[11px] text-muted-foreground">{p.code}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{p.category}</td>
                  <td className="px-4 py-3" data-testid={`product-tray-${p.code}`}>{p.tray ? <span className="font-mono text-[11px] px-2 py-0.5 rounded border border-gold/30 text-gold bg-gold/5">{p.tray}</span> : <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-4 py-3" data-testid={`product-group-${p.code}`}>{p.group ? <span className="text-[11px] px-2 py-0.5 rounded border border-purple-400/40 text-purple-300 bg-purple-500/10">{p.group}</span> : <span className="text-muted-foreground">Genel</span>}</td>
                  <td className="px-4 py-3 text-muted-foreground">{p.karat} / {fmtGram(p.gram)}g</td>
                  <td className="px-4 py-3 text-foreground">{p.milyem || 0} milyem</td>
                  <td className="px-4 py-3" data-testid={`product-status-${p.code}`}>
                    {p.draft ? (
                      <span className="text-[11px] px-2 py-1 rounded border text-amber-400 border-amber-500/30 bg-amber-500/10">Taslak</span>
                    ) : p.active ? (
                      <span className="text-[11px] px-2 py-1 rounded border text-emerald-400 border-emerald-500/30 bg-emerald-500/10">Aktif</span>
                    ) : (
                      <span className="text-[11px] px-2 py-1 rounded border text-muted-foreground border-border bg-elevated">Pasif</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] px-2 py-1 rounded border ${p.stock_status === "Stokta" ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-red-400 border-red-500/30 bg-red-500/10"}`}>{p.stock_status}</span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {(p.draft || !p.active) && (
                      <button data-testid={`activate-product-${p.code}`} onClick={() => activateProduct(p)} className="text-[11px] px-2 py-1 mr-1 rounded border border-gold/40 text-gold hover:bg-gold/10 transition-colors">
                        {p.draft ? "Yayınla" : "Aktifleştir"}
                      </button>
                    )}
                    <button data-testid={`edit-product-${p.code}`} onClick={() => openEdit(p)} className="text-muted-foreground hover:text-gold transition-colors p-1"><Pencil size={16} /></button>
                    <button data-testid={`delete-product-${p.code}`} onClick={() => setDelTarget(p)} className="text-muted-foreground hover:text-red-400 transition-colors p-1 ml-1"><Trash2 size={16} /></button>
                  </td>
                </tr>
                  ))}
                </Fragment>
              ))}
              {filtered.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-muted-foreground">Ürün bulunamadı.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-border text-foreground max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Ürün Düzenle" : "Yeni Ürün"}</DialogTitle></DialogHeader>

          <div>
            <label className="text-[11px] text-muted-foreground mb-1 block">Ürün Fotoğrafları (ilk görsel kapak · max 6)</label>
            <div className="flex items-center gap-2 flex-wrap">
              {(form.images || []).map((u, i) => (
                <div key={i} className="w-20 h-24 rounded-md overflow-hidden bg-obsidian border border-border shrink-0 relative">
                  <img src={imgUrl(u)} alt="" className="w-full h-full object-cover" />
                  {i === 0 && <span className="absolute bottom-0 inset-x-0 bg-gold text-obsidian text-[9px] text-center font-semibold">Kapak</span>}
                  <button onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, j) => j !== i) }))} className="absolute top-0.5 right-0.5 bg-black/70 rounded p-0.5" data-testid={`pf-img-remove-${i}`}><X size={12} /></button>
                </div>
              ))}
              {(form.images || []).length < 6 && (
                <>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="product-image-input" onChange={(e) => upload(e.target.files[0])} />
                  <button onClick={() => fileRef.current?.click()} disabled={uploading} className="w-20 h-24 rounded-md border border-dashed border-border flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-gold hover:text-gold transition-colors disabled:opacity-60" data-testid="pf-add-image">
                    {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}<span className="text-[10px]">Ekle</span>
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-3">
            <Field label="Ürün Adı (boş = otomatik: Ayar + Kategori)" span><Input data-testid="pf-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Boş bırakılırsa otomatik oluşturulur" className={inputCls} /></Field>
            <Field label="Ürün Kodu"><Input data-testid="pf-code" value={form.code} onChange={(e) => set("code", e.target.value)} className={inputCls} /></Field>
            <Field label="Barkod (Pusula)"><Input data-testid="pf-barcode" value={form.barcode} onChange={(e) => set("barcode", e.target.value)} className={inputCls} /></Field>
            <Field label="Tabla (Konum)"><Input data-testid="pf-tray" value={form.tray} onChange={(e) => set("tray", e.target.value)} placeholder="Örn. TABLA-01" className={inputCls} /></Field>
            <Field label="Grup">
              <Select value={form.group || "none"} onValueChange={(v) => set("group", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="pf-group" className={`${inputCls} h-9`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-surface border-border text-foreground">
                  <SelectItem value="none">Genel</SelectItem>
                  <SelectItem value="6007">6007 (yalnızca admin)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Kategori">
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="pf-category" className={`${inputCls} h-9`}><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent className="bg-surface border-border text-foreground max-h-64">
                  {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Ayar">
              <Select value={form.karat} onValueChange={(v) => set("karat", v)}>
                <SelectTrigger data-testid="pf-karat" className={`${inputCls} h-9`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-surface border-border text-foreground">
                  {["8", "14", "18", "22", "24"].map((k) => <SelectItem key={k} value={k}>{k} Ayar</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Tahmini Gram">
              {form.is_set ? (
                <Input data-testid="pf-gram" value={(form.set_parts || []).reduce((a, p) => a + (parseFloat(p.gram) || 0), 0).toFixed(2)} readOnly className={`${inputCls} opacity-70`} />
              ) : (
                <Input data-testid="pf-gram" type="number" step="0.01" value={form.gram} onChange={(e) => set("gram", e.target.value)} className={inputCls} />
              )}
            </Field>
            <Field label="İşçilik (Milyem)"><Input data-testid="pf-milyem" type="number" value={form.milyem} onChange={(e) => set("milyem", e.target.value)} className={inputCls} /></Field>
            <Field label="Tahmini Aralık (Min–Max gr, opsiyonel)" span>
              <div className="flex items-center gap-2">
                <Input data-testid="pf-gram-min" type="number" step="0.01" placeholder="Min gr" value={form.gram_min} onChange={(e) => set("gram_min", e.target.value)} className={inputCls} />
                <span className="text-muted-foreground">–</span>
                <Input data-testid="pf-gram-max" type="number" step="0.01" placeholder="Max gr" value={form.gram_max} onChange={(e) => set("gram_max", e.target.value)} className={inputCls} />
              </div>
            </Field>
            <Field label="Parmak Boyları (virgülle: 44,45,46)" span>
              <Input data-testid="pf-sizes" placeholder="örn. 44, 45, 46" value={form.sizes} onChange={(e) => set("sizes", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Set Ürün" span>
              <label className="flex items-center gap-2 mb-2 cursor-pointer text-sm">
                <input type="checkbox" data-testid="pf-is-set" checked={!!form.is_set} onChange={(e) => set("is_set", e.target.checked)} className="accent-gold w-4 h-4" />
                Bu bir set ürün (parça parça gram — ayar/milyem set geneli)
              </label>
              {form.is_set && (
                <div className="space-y-2">
                  {(form.set_parts || []).map((p, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <Input data-testid={`pf-part-name-${i}`} placeholder="Parça (örn. Bileklik)" value={p.name} onChange={(e) => setForm((f) => ({ ...f, set_parts: f.set_parts.map((x, j) => j === i ? { ...x, name: e.target.value } : x) }))} className={inputCls} />
                      <Input data-testid={`pf-part-gram-${i}`} type="number" step="0.01" placeholder="Gram" value={p.gram} onChange={(e) => setForm((f) => ({ ...f, set_parts: f.set_parts.map((x, j) => j === i ? { ...x, gram: e.target.value } : x) }))} className={`${inputCls} w-28`} />
                      <button type="button" onClick={() => setForm((f) => ({ ...f, set_parts: f.set_parts.filter((_, j) => j !== i) }))} className="text-muted-foreground hover:text-red-400 p-1"><X size={16} /></button>
                    </div>
                  ))}
                  <button type="button" data-testid="pf-add-part" onClick={() => setForm((f) => ({ ...f, set_parts: [...(f.set_parts || []), { name: "", gram: "" }] }))} className="flex items-center gap-1 text-sm text-gold hover:underline"><Plus size={14} /> Parça Ekle</button>
                  <p className="text-[11px] text-muted-foreground">Toplam gram: <b className="text-gold">{(form.set_parts || []).reduce((a, p) => a + (parseFloat(p.gram) || 0), 0).toFixed(2)} gr</b></p>
                </div>
              )}
            </Field>
            <Field label="Stok Durumu">
              <Select value={form.stock_status} onValueChange={(v) => set("stock_status", v)}>
                <SelectTrigger data-testid="pf-stock" className={`${inputCls} h-9`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-surface border-border text-foreground">
                  <SelectItem value="Stokta">Stokta</SelectItem>
                  <SelectItem value="Stokta Yok">Stokta Yok</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Alt Kategoriler (ops. — birden fazla seçebilirsiniz)" span>
              {(() => {
                const cat = categories.find((c) => c.name === form.category);
                const opts = cat?.subcategories || [];
                if (opts.length === 0) return <p className="text-xs text-muted-foreground">Bu kategori için alt kategori tanımlı değil (Kategoriler sayfasından ekleyebilirsiniz).</p>;
                const sel = form.subcategories?.length ? form.subcategories : (form.subcategory ? [form.subcategory] : []);
                const toggle = (s) => set("subcategories", sel.includes(s) ? sel.filter((x) => x !== s) : [...sel, s]);
                return (
                  <div className="flex flex-wrap gap-2" data-testid="pf-subcategories">
                    {opts.map((s) => {
                      const on = sel.includes(s);
                      return <button type="button" key={s} data-testid={`pf-subcat-${s}`} onClick={() => toggle(s)} className={`px-3 h-8 rounded-full text-xs border transition-colors ${on ? "bg-gold text-obsidian border-gold" : "border-border text-muted-foreground hover:border-gold hover:text-gold"}`}>{s}</button>;
                    })}
                  </div>
                );
              })()}
            </Field>
            <Field label="Marka (ops. — AI marka uydurmaz, sadece siz girersiniz)">
              <Input data-testid="pf-brand" value={form.brand || ""} onChange={(e) => set("brand", e.target.value)} placeholder="Örn. Cartier" className={`${inputCls} h-9`} />
            </Field>
            <Field label="Model / Koleksiyon (ops.)">
              <Input data-testid="pf-model" value={form.model || ""} onChange={(e) => set("model", e.target.value)} placeholder="Örn. Love" className={`${inputCls} h-9`} />
            </Field>
            <Field label="Alt-Alt Kategori (ops.)" span>
              {(() => {
                const cat = categories.find((c) => c.name === form.category);
                const sc = cat?.sub_children || {};
                const sel = form.subcategories?.length ? form.subcategories : (form.subcategory ? [form.subcategory] : []);
                const opts = [];
                sel.forEach((s) => (sc[s] || []).forEach((ch) => { if (!opts.includes(ch)) opts.push(ch); }));
                if (opts.length === 0) return <p className="text-xs text-muted-foreground">Seçili alt kategorinin alt-alt kategorisi yok.</p>;
                return (
                  <div className="flex flex-wrap gap-2" data-testid="pf-subsubs">
                    <button type="button" data-testid="pf-subsub-none" onClick={() => set("subsub", "")} className={`px-3 h-8 rounded-full text-xs border transition-colors ${!form.subsub ? "bg-gold text-obsidian border-gold" : "border-border text-muted-foreground hover:border-gold hover:text-gold"}`}>Yok</button>
                    {opts.map((s) => {
                      const on = form.subsub === s;
                      return <button type="button" key={s} data-testid={`pf-subsub-${s}`} onClick={() => set("subsub", on ? "" : s)} className={`px-3 h-8 rounded-full text-xs border transition-colors ${on ? "bg-gold text-obsidian border-gold" : "border-border text-muted-foreground hover:border-gold hover:text-gold"}`}>{s}</button>;
                    })}
                  </div>
                );
              })()}
            </Field>
            <div className="col-span-2 flex items-center justify-between bg-obsidian border border-border rounded-md px-3 py-2.5">
              <span className="text-sm">Aktif (müşterilere görünür)</span>
              <Switch data-testid="pf-active" checked={form.active} onCheckedChange={(v) => set("active", v)} />
            </div>
          </div>

          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 h-10 border border-border rounded-md text-muted-foreground hover:text-foreground transition-colors">İptal</button>
            <button data-testid="save-product-button" onClick={save} disabled={saving} className="px-5 h-10 bg-gold text-obsidian font-semibold rounded-md hover:bg-gold-hover transition-colors flex items-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="animate-spin" size={16} />} Kaydet
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="bg-surface border-border text-foreground max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">Toplu Ürün İçe Aktar</DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground space-y-2">
            <p>Excel (.xlsx) veya CSV dosyasıyla binlerce ürünü tek seferde yükleyin. Aynı <b>Ürün Kodu</b> tekrar gelirse mevcut ürün güncellenir; olmayan kategoriler otomatik oluşturulur.</p>
            <p>Sütunlar: <span className="text-foreground">Ürün Adı, Ürün Kodu, Kategori, Ayar, Gram, Milyem, Stok Durumu, Fotoğraf URL, Aktif</span></p>
          </div>

          <div className="flex items-center gap-2 mt-1">
            <button onClick={downloadTemplate} disabled={dlTemplate} className="flex items-center gap-2 px-3 h-9 text-sm border border-border rounded-md hover:border-gold transition-colors disabled:opacity-60">
              {dlTemplate ? <Loader2 className="animate-spin" size={15} /> : <Download size={15} />} Örnek Şablonu İndir
            </button>
          </div>

          <input ref={importRef} type="file" accept=".xlsx,.xlsm,.csv" className="hidden" data-testid="import-file-input" onChange={(e) => runImport(e.target.files[0])} />
          <button
            data-testid="import-file-button"
            onClick={() => importRef.current?.click()}
            disabled={importing}
            className="w-full h-24 border-2 border-dashed border-border rounded-md flex flex-col items-center justify-center gap-2 text-muted-foreground hover:border-gold hover:text-gold transition-colors disabled:opacity-60"
          >
            {importing ? <Loader2 className="animate-spin" size={22} /> : <FileSpreadsheet size={22} />}
            <span className="text-sm">{importing ? "Yükleniyor, lütfen bekleyin..." : "Dosya seçin (.xlsx / .csv)"}</span>
          </button>

          {importResult && (
            <div data-testid="import-result" className="bg-obsidian border border-border rounded-md p-4 text-sm">
              <div className="flex items-center gap-2 text-emerald-400 mb-2">
                <CheckCircle2 size={16} /> İçe aktarma tamamlandı
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div><div className="text-xl font-semibold text-gold">{importResult.added}</div><div className="text-[11px] text-muted-foreground">Eklendi</div></div>
                <div><div className="text-xl font-semibold text-foreground">{importResult.updated}</div><div className="text-[11px] text-muted-foreground">Güncellendi</div></div>
                <div><div className="text-xl font-semibold text-red-400">{importResult.error_count}</div><div className="text-[11px] text-muted-foreground">Hata</div></div>
              </div>
              {importResult.categories_created?.length > 0 && (
                <p className="mt-3 text-[11px] text-muted-foreground">Yeni kategoriler: {importResult.categories_created.join(", ")}</p>
              )}
              {importResult.errors?.length > 0 && (
                <div className="mt-3 max-h-32 overflow-y-auto space-y-1">
                  {importResult.errors.map((e, i) => (
                    <div key={i} className="text-[11px] text-red-400">Satır {e.row}: {e.msg}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <button onClick={() => setImportOpen(false)} className="px-4 h-10 border border-border rounded-md text-muted-foreground hover:text-foreground transition-colors">Kapat</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={bulkDelOpen} onOpenChange={setBulkDelOpen}>
        <AlertDialogContent className="bg-surface border-border text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>Seçili ürünleri sil?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">{selected.size} ürün kalıcı olarak silinecek. Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-border text-foreground hover:bg-elevated">Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-bulk-delete" onClick={bulkDelete} className="bg-red-500 text-white hover:bg-red-600">Sil ({selected.size})</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent className="bg-surface border-border text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>Ürünü sil?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">{delTarget?.name} ({delTarget?.code}) kalıcı olarak silinecek.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-border text-foreground hover:bg-elevated">Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-product" onClick={doDelete} className="bg-red-500 text-white hover:bg-red-600">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={bulkEditOpen} onOpenChange={setBulkEditOpen}>
        <DialogContent className="bg-surface border-border text-foreground max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bulk-edit-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Toplu Düzenle ({selected.size} ürün)</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Yalnızca doldurduğunuz alanlar seçili ürünlere uygulanır; boş bırakılanlar değişmez.</p>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div className="col-span-2">
              <label className="text-xs text-muted-foreground">Ürün Adı</label>
              <Input data-testid="be-name" value={bEdit.name || ""} onChange={(e) => bset("name", e.target.value)} placeholder="— Değiştirme (boş bırak)" className={inputCls} />
            </div>
            <Field label="Ayar">
              <Select value={bEdit.karat || "none"} onValueChange={(v) => bset("karat", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="be-karat" className={inputCls}><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-surface border-border text-foreground">
                  <SelectItem value="none">— Değiştirme</SelectItem>
                  {["8", "14", "18", "22", "24"].map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Milyem"><Input data-testid="be-milyem" type="number" inputMode="numeric" value={bEdit.milyem ?? ""} onChange={(e) => bset("milyem", e.target.value)} placeholder="—" className={inputCls} /></Field>
            <Field label="Kategori">
              <Select value={bEdit.category || "none"} onValueChange={(v) => { bset("category", v === "none" ? "" : v); bset("subcategory", ""); }}>
                <SelectTrigger data-testid="be-category" className={inputCls}><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-surface border-border text-foreground max-h-64">
                  <SelectItem value="none">— Değiştirme</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Alt Kategori">
              <SubcategoryInput categories={categories} category={bEdit.category} value={bEdit.subcategory || ""} onChange={(v) => bset("subcategory", v)} className={inputCls} testid="be-subcategory" />
            </Field>
            {!fixedGroup && (
              <Field label="Grup">
                <Select value={bEdit.group || "none"} onValueChange={(v) => bset("group", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="be-group" className={inputCls}><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-surface border-border text-foreground">
                    <SelectItem value="none">— Değiştirme</SelectItem>
                    <SelectItem value="genel">Genel</SelectItem>
                    <SelectItem value="6007">6007</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            )}
            <Field label="Tabla (Konum)"><Input data-testid="be-tray" value={bEdit.tray ?? ""} onChange={(e) => bset("tray", e.target.value)} placeholder="—" className={inputCls} /></Field>
            <Field label="Stok Durumu">
              <Select value={bEdit.stock_status || "none"} onValueChange={(v) => bset("stock_status", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="be-stock" className={inputCls}><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-surface border-border text-foreground">
                  <SelectItem value="none">— Değiştirme</SelectItem>
                  <SelectItem value="Stokta">Stokta</SelectItem>
                  <SelectItem value="Stokta Yok">Stokta Yok</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Durum">
              <Select value={bEdit.active || "none"} onValueChange={(v) => bset("active", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="be-active" className={inputCls}><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-surface border-border text-foreground">
                  <SelectItem value="none">— Değiştirme</SelectItem>
                  <SelectItem value="active">Aktif</SelectItem>
                  <SelectItem value="passive">Pasif</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <button onClick={() => setBulkEditOpen(false)} className="px-4 h-10 border border-border rounded-md text-muted-foreground hover:text-foreground transition-colors">İptal</button>
            <button data-testid="apply-bulk-edit" onClick={applyBulkEdit} className="px-5 h-10 bg-gold text-obsidian font-semibold rounded-md hover:bg-gold-hover transition-colors">Uygula ({selected.size})</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {lightbox && (
        <div
          className="fixed inset-0 z-[120] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
          onClick={() => setLightbox(null)}
          data-testid="image-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            data-testid="lightbox-close"
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
          >
            <X size={20} />
          </button>
          <img
            src={lightbox.images[lightbox.idx]}
            alt=""
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
            className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
          />
          {lightbox.images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); lbNav(-1); }}
                data-testid="lightbox-prev"
                className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); lbNav(1); }}
                data-testid="lightbox-next"
                className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              >
                <ChevronRight size={24} />
              </button>
              <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/80 text-sm font-mono">
                {lightbox.idx + 1} / {lightbox.images.length}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, span, children }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <label className="text-[11px] text-muted-foreground mb-1 block">{label}</label>
      {children}
    </div>
  );
}
